import { SasToken } from 'gf-types';
import MicRecorder from 'mic-recorder-to-mp3';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import GfModal from '../../../gfcomponents/GfModal/GfModal';
import { analyticsTrack } from '../../../helpers/AnalyticsUtility';
import { uploadFileFromBrowser } from '../../../helpers/FileUtility';
import { useAppSelector } from '../../../hooks/ReduxHooks';
import { useActions } from '../../../hooks/useActions';
import { DownloadIcon } from '../../../icons/icons';
import { getSharedFormImageUrl } from '../../actions';
import { AudioValue, ValueChangedForFieldFunc } from '../../formeditor-types';
import { convertSecondsToTime } from '../../helpers';
import './AudioModal.scss';

export type AudioModalProps = {
  name?: string;
  value?: AudioValue;
  valueUpdated?: ValueChangedForFieldFunc;
  onClose?: () => void;
  maxLength: number;
  formId?: string;
  fieldId?: string;
  editable?: boolean;
};

export default function AudioModal(props: AudioModalProps) {
  const isPublicForm = !!useAppSelector((state) => state.formeditor.sharedAccessToken);
  const sasToken = useAppSelector<SasToken>((state) => state.shared.sasToken);
  const fileUrls = useAppSelector<Record<string, string>>((state) => state.formeditor.fileUrls);
  const getSharedFormImageUrlAction = useActions(getSharedFormImageUrl);
  const recorder = useMemo<MicRecorder>(() => new MicRecorder({ bitRate: 128 }), []);
  const [value, setValue] = useState<AudioValue>(props.value || null);
  const [recordingLength, setRecordingLength] = useState<number>(0);
  const [hasMicAccess, setHasMicAccess] = useState<boolean>(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timer>();
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const [blobUrl, setBlobUrl] = useState<string>(() => {
    if (!!value) {
      if (!!fileUrls) {
        return fileUrls[value.fileId];
      }

      if (!!sasToken) {
        return `${sasToken.baseUrl}${value.fileId}${sasToken.token}`;
      }
    }

    return '';
  });

  useEffect(() => {
    if (isRecording && recordingLength > props.maxLength * 60) {
      onStopRecording();
    }
  }, [recordingLength, props.maxLength]);

  useEffect(() => {
    if (!!audioRef?.current) {
      audioRef.current.ondurationchange = () => {
        setValue((v) => ({ ...v, length: audioRef.current?.duration }));
      };
      audioRef.current.ontimeupdate = () => {
        if (!audioRef.current?.paused) {
          setRecordingLength(audioRef.current?.currentTime);
        }
      };
      audioRef.current.onseeking = (e) => {
        setRecordingLength((e.target as HTMLAudioElement).currentTime);
      };
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setRecordingLength(audioRef.current?.duration);
      };
    }
  }, [audioRef.current]);

  const onStartRecording = () => {
    recorder
      .start()
      .then(() => {
        setIsRecording(true);
        setRecordingLength(0);
        setTimer(setInterval(() => setRecordingLength((l) => l + 1), 1000));
        if (!hasMicAccess) {
          setHasMicAccess(true);
        }
      })
      .catch(() => {
        setHasMicAccess(false);
      });
  };

  const onStopRecording = () => {
    clearInterval(timer);
    setIsRecording(false);
    recorder
      .stop()
      .getMp3()
      .then(([_buffer, blob]: [[], Blob]) => {
        setBlobUrl(URL.createObjectURL(blob));
        const now = new Date();
        setValue({
          name: `GoFormzAudio_${now.getFullYear()}${now.getMonth() + 1}${now.getDate()}_${now.getHours()}${now.getMinutes()}${now.getSeconds()}.mp3`,
          size: blob.size,
          length: recordingLength,
          timeStamp: now.toISOString(),
          fileId: undefined,
          type: blob.type
        });
        setRecordingLength(0);
        setIsDirty(true);
      });
  };

  const onStartPlayback = () => {
    setIsPlaying(true);
    audioRef.current.play();
  };

  const onStopPlayback = () => {
    setIsPlaying(false);
    audioRef.current.pause();
  };

  const skipTime = (skipBy: number) => {
    audioRef.current.currentTime = audioRef.current.currentTime + skipBy;
  };

  const getControls = () => {
    if (!value && props.editable) {
      return !isRecording ? (
        <div className="primary-button record-start" onClick={onStartRecording} />
      ) : (
        <div className="primary-button record-stop" onClick={onStopRecording} />
      );
    } else {
      return (
        <>
          <div className="rewind-button" onClick={() => skipTime(-10)} />
          {!isPlaying ? (
            <div className="primary-button playback-play" onClick={onStartPlayback} />
          ) : (
            <div className="primary-button playback-pause" onClick={onStopPlayback} />
          )}
          <div className="fast-forward-button" onClick={() => skipTime(10)} />
        </>
      );
    }
  };

  const onDoneClick = async () => {
    if (isDirty) {
      if (!value) {
        props.valueUpdated({ value: null });
        props.onClose();
      } else {
        setIsSaving(true);
        const file = await fetch(blobUrl)
          .then((r) => r.blob())
          .then((blob) => new File([blob], value.name, { type: blob.type }));
        uploadFileFromBrowser(file, false, file.name, null, props.formId, null, null, null, 0, true, props.fieldId, false).then((uploadResult) => {
          const fileId = uploadResult.fileId;

          if (isPublicForm) {
            getSharedFormImageUrlAction(uploadResult.fileId);
          }

          setIsSaving(false);
          props.valueUpdated({ value: JSON.stringify({ ...value, fileId: fileId }) });
          //Trigger: User captures and saves audio on the form.
          analyticsTrack('Audio Capture Field - Audio Captured', {});
          props.onClose();
        });
      }
    } else {
      props.onClose();
    }
  };

  const onClearClick = () => {
    if (isPlaying) {
      onStopPlayback();
    }
    setValue(null);
    setRecordingLength(0);
    setIsDirty(true);
  };

  const onClose = () => {
    if (isRecording) {
      recorder.stop();
    }

    props.onClose();
  };

  return (
    <GfModal
      className="audio-modal"
      title={props.name}
      primaryText="Done"
      secondaryText={!!value && props.editable ? 'Clear' : undefined}
      onPrimaryClick={onDoneClick}
      onSecondaryClick={onClearClick}
      onClose={onClose}
      actionButtonProps={{ primary: { disabled: isRecording || isSaving } }}
      semanticUiModalProps={{ size: 'tiny', open: true }}
      loading={isSaving}
    >
      {value || props.editable ? (
        <div className="recorder-body">
          <div className="file-row">
            {!!value && (
              <>
                <span className="file-name">{value.name}</span>
                <a className="download-link" href={blobUrl} download={value.name} target="_blank">
                  <DownloadIcon />
                </a>
              </>
            )}
          </div>
          <div className="progress-bar-container">
            {(isRecording || !!value) && (
              <>
                <div className="progress-bar-background" />
                <div
                  className={`progress-bar ${!value ? 'recording' : 'playback'}`}
                  style={{
                    width: `${(Math.floor(recordingLength) / (!value ? props.maxLength * 60 : Math.floor(value.length))) * 100}%`
                  }}
                />
              </>
            )}
            {!!blobUrl && <audio src={blobUrl} ref={audioRef} />}
          </div>
          <div className="time-display">
            <span className="current-time">{convertSecondsToTime(recordingLength)}</span>
            <span className={`total-time ${!value ? 'recording' : ''}`}>{` / ${!value ? props.maxLength + ':00' : convertSecondsToTime(value.length)}`}</span>
          </div>
          <div className="controls">{getControls()}</div>
          <div className="message">
            {isRecording && <span className="recording-message">Stop Recording</span>}
            {!hasMicAccess && (
              <span className="mic-permission-error">
                You've denied access to your microphone.
                <br />
                To submit audio using this field, you'll need to allow microphone access.
              </span>
            )}
          </div>
        </div>
      ) : null}
    </GfModal>
  );
}
